import { useEffect, useRef, useState } from 'react';

/**
 * 교육 카테고리 분류(class_categories) 단계별 드롭다운
 * - 관리자모드(/admin/education-categories)에서 설정한 1·2·3차 트리를 그대로 사용
 * - 1차만 있으면 1차에서 끝, 2차가 있으면 1차 선택 후 2차 노출, 3차가 있으면 2차 선택 후 3차 노출
 * - 선택된 "가장 깊은" 노드 id를 categoryId로 보고한다.
 * - complete: 선택한 노드에 더 이상 하위가 없는 경우(=말단까지 선택) true
 */

interface CategoryNode {
  id: string;
  parent_id: string | null;
  name: string;
  level: 1 | 2 | 3;
  sort_order: number;
}

interface ClassCategorySelectProps {
  value: string | null;
  onChange: (categoryId: string | null, complete: boolean) => void;
  required?: boolean;
}

export default function ClassCategorySelect({ value, onChange, required }: ClassCategorySelectProps) {
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [l1, setL1] = useState('');
  const [l2, setL2] = useState('');
  const [l3, setL3] = useState('');
  const initializedRef = useRef(false);

  // 카테고리 트리 로드
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch('/api/v1/categories', { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch categories');
        const data = await res.json();
        setCategories((data.data as CategoryNode[]) || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const childrenOf = (parentId: string | null) =>
    categories
      .filter((c) => c.parent_id === parentId)
      .sort((a, b) => a.sort_order - b.sort_order);

  // 기존 값으로 초기 선택 복원 (수정 페이지) — 한 번만
  useEffect(() => {
    if (initializedRef.current || categories.length === 0) return;
    initializedRef.current = true;
    if (!value) return;

    const byId = new Map(categories.map((c) => [c.id, c]));
    const node = byId.get(value);
    if (!node) return;

    // 선택 노드에서 부모로 거슬러 올라가며 단계 채우기
    const chain: CategoryNode[] = [];
    let cur: CategoryNode | undefined = node;
    while (cur) {
      chain.unshift(cur);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    setL1(chain[0]?.id ?? '');
    setL2(chain[1]?.id ?? '');
    setL3(chain[2]?.id ?? '');
  }, [categories, value]);

  // 선택 변경을 부모에 보고
  const report = (deepestId: string) => {
    if (!deepestId) {
      onChange(null, false);
      return;
    }
    const complete = childrenOf(deepestId).length === 0;
    onChange(deepestId, complete);
  };

  const handleL1 = (id: string) => {
    setL1(id);
    setL2('');
    setL3('');
    report(id);
  };

  const handleL2 = (id: string) => {
    setL2(id);
    setL3('');
    report(id || l1);
  };

  const handleL3 = (id: string) => {
    setL3(id);
    report(id || l2);
  };

  const selectClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white disabled:bg-gray-100';

  const l1Options = childrenOf(null);
  const l2Options = l1 ? childrenOf(l1) : [];
  const l3Options = l2 ? childrenOf(l2) : [];

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-900 mb-1.5">
        교육 카테고리 {required && <span className="text-red-500">*</span>}
      </label>

      {loading ? (
        <div className="animate-pulse h-10 bg-gray-200 rounded-lg" />
      ) : l1Options.length === 0 ? (
        <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-xs">
          등록된 카테고리가 없습니다. 관리자에게 문의해주세요.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {/* 1차 */}
          <select value={l1} onChange={(e) => handleL1(e.target.value)} className={selectClass}>
            <option value="">대분류 선택</option>
            {l1Options.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/* 2차 (있을 때만) */}
          {l2Options.length > 0 && (
            <select value={l2} onChange={(e) => handleL2(e.target.value)} className={selectClass}>
              <option value="">중분류 선택</option>
              {l2Options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}

          {/* 3차 (있을 때만) */}
          {l3Options.length > 0 && (
            <select value={l3} onChange={(e) => handleL3(e.target.value)} className={selectClass}>
              <option value="">소분류 선택</option>
              {l3Options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-1.5">
        관리자가 설정한 분류에서 가장 하위 단계까지 선택해주세요.
      </p>
    </div>
  );
}
